/* eslint-disable react-hooks/exhaustive-deps */
import { Table, Button, Space, Tag, Select, Input, DatePicker, Row, Col, Typography } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import ClaimDetailModal from "./ClaimDetailModal";
import {
  REWARD_CLAIM_STATUS_ENUM,
  CLAIM_STATUS_TAG_COLORS,
  buildClaimsCsv,
  downloadCsvFile,
  formatEnumLabel,
  formatItemKeysList
} from "./helper";
import UpdateStatusModal from "./UpdateStatusModal";
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification
} from "../../utils/notification";

const BASE_URL = "admin/reward-kit-claims";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const DEFAULT_FILTERS = Object.freeze({
  status: undefined,
  courseUrlIdentifier: undefined,
  email: "",
  phone: "",
  dateRange: null
});

/**
 * Admin table of reward-kit claims with filters, a CSV export, a detail modal and a status
 * update modal. Filters apply only on "Apply Filters", which also returns to page 1. The
 * export honours the applied filters, not the draft ones, and ignores pagination.
 *
 * @param {object} props
 * @param {object} [props.adminConfig] - The admin's `rewardKitClaims` access config. `hasAccess`
 *   gates every read, including the export. `hasModifyAccess` shows the Update Status action.
 * @param {string} props.ROOT_URL - Backend origin for the selected environment.
 */
const RewardKitClaimsDash = ({ adminConfig = {}, ROOT_URL }) => {
  const [claimsList, setClaimsList] = useState([]);
  const [isLoadingClaimsList, setIsLoadingClaimsList] = useState(false);
  const [filtersDraft, setFiltersDraft] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [isExportingCsv, setIsExportingCsv] = useState(false);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [isClaimDetailModalOpen, setIsClaimDetailModalOpen] = useState(false);

  const [selectedClaimForStatusUpdate, setSelectedClaimForStatusUpdate] = useState(null);
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);

  /**
   * Builds the query params for the list and export endpoints from the applied filters.
   * Empty filters are sent as `undefined`, and the date range spans from the start of the
   * first day to the end of the last day.
   *
   * @param {object} [extraParams] - Params merged last, such as `page` and `limit`.
   * @returns {object}
   */
  const buildQueryParams = (extraParams = {}) => {
    const { status, courseUrlIdentifier, email, phone, dateRange } = appliedFilters;
    return {
      status: status || undefined,
      courseUrlIdentifier: courseUrlIdentifier || undefined,
      email: email || undefined,
      phone: phone || undefined,
      dateFrom: dateRange?.[0] ? dateRange[0].startOf("day").toISOString() : undefined,
      dateTo: dateRange?.[1] ? dateRange[1].endOf("day").toISOString() : undefined,
      ...extraParams
    };
  };

  /** Loads the current page of claims for the applied filters. */
  const fetchClaimsList = () => {
    setIsLoadingClaimsList(true);
    axios
      .get(`${ROOT_URL}/${BASE_URL}/list`, {
        params: buildQueryParams({ page: pagination.current, limit: pagination.pageSize })
      })
      .then((response) => {
        const { data: claims, pagination: paginationMeta } = response.data;
        setClaimsList(claims || []);
        setPagination((previousPagination) => ({
          ...previousPagination,
          total: paginationMeta?.totalItems || 0
        }));
      })
      .catch((error) => {
        showErrorNotification(error);
      })
      .finally(() => {
        setIsLoadingClaimsList(false);
      });
  };

  /** Applies the draft filters and returns to the first page. */
  const handleApplyFilters = () => {
    setAppliedFilters(filtersDraft);
    setPagination((previousPagination) => ({ ...previousPagination, current: 1 }));
  };

  /** Clears both the draft and applied filters and returns to the first page. */
  const handleResetFilters = () => {
    setFiltersDraft(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPagination((previousPagination) => ({ ...previousPagination, current: 1 }));
  };

  /**
   * Downloads every claim matching the applied filters as a CSV file. An empty result shows
   * a warning notification and downloads nothing.
   */
  const handleExportCsv = () => {
    setIsExportingCsv(true);
    axios
      .get(`${ROOT_URL}/${BASE_URL}/export`, {
        params: buildQueryParams()
      })
      .then((response) => {
        const claimsToExport = Array.isArray(response.data) ? response.data : [];
        if (claimsToExport.length === 0) {
          showWarningNotification("No reward kit claims found matching the selected filters");
          return;
        }

        downloadCsvFile(
          buildClaimsCsv(claimsToExport),
          `reward_kit_claims_${dayjs().format("YYYY-MM-DD_HHmmss")}.csv`
        );
        showSuccessNotification("Reward kit claims exported successfully");
      })
      .catch((error) => {
        showErrorNotification(error);
      })
      .finally(() => {
        setIsExportingCsv(false);
      });
  };

  /**
   * Stores the page and page size the table paginator chose.
   *
   * @param {{current: number, pageSize: number}} paginationConfig
   */
  const handleTableChange = ({ current, pageSize }) => {
    setPagination((previousPagination) => ({ ...previousPagination, current, pageSize }));
  };

  /**
   * Builds the change handler for one draft filter field.
   *
   * @param {string} filterName - The `filtersDraft` key the handler writes to.
   * @returns {function(*): void} Receives the new value.
   */
  const handleDraftFilterChange = (filterName) => (value) => {
    setFiltersDraft((previousFilters) => ({ ...previousFilters, [filterName]: value }));
  };

  /**
   * Builds the change handler for one draft filter backed by a text input.
   *
   * @param {string} filterName - The `filtersDraft` key the handler writes to.
   * @returns {function(Event): void}
   */
  const handleDraftTextFilterChange = (filterName) => (event) => {
    const { value } = event.target;
    setFiltersDraft((previousFilters) => ({ ...previousFilters, [filterName]: value }));
  };

  /**
   * Opens the detail modal for one claim.
   *
   * @param {string} claimUniquePublicId
   */
  const handleViewClaim = (claimUniquePublicId) => {
    setSelectedClaimId(claimUniquePublicId);
    setIsClaimDetailModalOpen(true);
  };

  /**
   * Opens the status update modal for one claim.
   *
   * @param {object} claim - The claim row.
   */
  const handleOpenStatusUpdate = (claim) => {
    setSelectedClaimForStatusUpdate(claim);
    setIsUpdateStatusModalOpen(true);
  };

  const columns = [
    {
      title: "Course",
      key: "course",
      render: ({ courseName, courseUrlIdentifier }) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text strong style={{ fontSize: "14px" }}>
            {courseName || "TLE Prime"}
          </Text>
          {courseUrlIdentifier && (
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {courseUrlIdentifier}
            </Text>
          )}
        </div>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={CLAIM_STATUS_TAG_COLORS[status]}>{formatEnumLabel(status)}</Tag>
    },
    {
      title: "User Details",
      key: "userDetails",
      render: ({ shippingDetails }) => {
        const { fullName, email, phone } = shippingDetails || {};
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <Text strong>{fullName}</Text>
            <Text style={{ fontSize: "12px" }}>{email}</Text>
            <Text style={{ fontSize: "12px" }}>{phone}</Text>
          </div>
        );
      }
    },
    {
      title: "Items",
      key: "items",
      render: ({ items }) => formatItemKeysList(items)
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => (createdAt ? dayjs(createdAt).format("DD MMM YYYY | HH:mm") : "-")
    },
    {
      title: "Actions",
      key: "actions",
      render: (claim) => (
        <Space wrap>
          <Button type="primary" onClick={() => handleViewClaim(claim.uniquePublicId)}>
            View
          </Button>
          {adminConfig?.hasModifyAccess && (
            <Button onClick={() => handleOpenStatusUpdate(claim)}>Update Status</Button>
          )}
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchClaimsList();
  }, [appliedFilters, pagination.current, pagination.pageSize]);

  return (
    <div style={{ marginTop: "20px", marginLeft: "clamp(12px, 3vw, 30px)", marginRight: "clamp(12px, 3vw, 30px)" }}>
      <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
        <Col xs={24} sm={12} lg={4}>
          <Select
            placeholder="Status"
            allowClear
            style={{ width: "100%" }}
            value={filtersDraft.status}
            options={Object.values(REWARD_CLAIM_STATUS_ENUM).map((status) => ({
              value: status,
              label: formatEnumLabel(status)
            }))}
            onChange={handleDraftFilterChange("status")}
          />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Select
            placeholder="Select Course"
            allowClear
            style={{ width: "100%" }}
            value={filtersDraft.courseUrlIdentifier}
            options={[
              { value: "prime-foundation", label: "TLE Prime Foundation" },
              { value: "prime-pro", label: "TLE Prime Pro" }
            ]}
            onChange={handleDraftFilterChange("courseUrlIdentifier")}
          />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Input
            placeholder="Email"
            value={filtersDraft.email}
            onChange={handleDraftTextFilterChange("email")}
          />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Input
            placeholder="Phone"
            value={filtersDraft.phone}
            onChange={handleDraftTextFilterChange("phone")}
          />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <RangePicker
            style={{ width: "100%" }}
            value={filtersDraft.dateRange}
            onChange={handleDraftFilterChange("dateRange")}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: "16px" }}>
        <Space wrap>
          <Button type="primary" onClick={handleApplyFilters}>
            Apply Filters
          </Button>
          <Button onClick={handleResetFilters}>Reset</Button>
          {adminConfig?.hasAccess && (
            <Button onClick={handleExportCsv} loading={isExportingCsv}>
              Export CSV
            </Button>
          )}
        </Space>
      </Row>
      <Table
        columns={columns}
        dataSource={claimsList}
        rowKey="uniquePublicId"
        bordered
        loading={isLoadingClaimsList}
        scroll={{ x: 1000 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true
        }}
        onChange={handleTableChange}
      />
      {isClaimDetailModalOpen && (
        <ClaimDetailModal
          isModalOpen={isClaimDetailModalOpen}
          setIsModalOpen={setIsClaimDetailModalOpen}
          claimId={selectedClaimId}
          ROOT_URL={ROOT_URL}
          BASE_URL={BASE_URL}
        />
      )}
      {isUpdateStatusModalOpen && (
        <UpdateStatusModal
          isModalOpen={isUpdateStatusModalOpen}
          setIsModalOpen={setIsUpdateStatusModalOpen}
          claim={selectedClaimForStatusUpdate}
          ROOT_URL={ROOT_URL}
          BASE_URL={BASE_URL}
          onStatusUpdated={fetchClaimsList}
        />
      )}
    </div>
  );
};

export default RewardKitClaimsDash;
